import React, { useMemo } from 'react';
import T from 'prop-types';
import { isNil, nth } from 'lodash-es';

import ChartLayout from '../ChartLayout';
import { ActivityMetricDropdown } from '../MetricLists';
import Button from '../../../../../../components/Buttons/Button';
import { SmartChart } from '../../../../../../components/Chart';

import { baseMetrics } from '../../../../config/constants';
import { intraPeriodKey, periodChangeTypeCaptures } from '../../../../config/period-change-type';
import { baseDriversConfig } from '../../../../config/charts';

const emptyArray = [];
const positiveColor = '#3E8EDE';
const negativeColor = '#E33963';

// Used custom 'findLastIndex' because screenshot doesn't support native findLastIndex
const findLastPointWithValueIndex = (series) => {
  let index = -1;
  for (let i = 0; i < series.length; i++) {
    if (!isNil(series[i][1])) {
      index = i;
    }
  }
  return index;
};

// Note: Used in the Company page and QV
const BaseChart = ({
  sectionKey,
  chartSectionConfig,
  data,
  overPeriod,
  granularity = 'quarterly',
  cohortLabel = 'All Users',
  showActivePoint,
  headerContent = null,
  showFooter = true,
  chartStyleOverride,
  onMetricChange,
  onExpand,
}) => {
  const { expanded = false, metric } = chartSectionConfig;

  const seriesByMetric = (() => {
    const series = data.metrics?.[metric] || data.metrics?.[`${metric}_change`];
    // Point structure: [timestamp, value]
    const hasData = series?.some((item) => !isNil(item[1]));
    return hasData ? series : emptyArray;
  })();

  const isIntraPeriod = overPeriod === intraPeriodKey;

  const hasPartialPeriod = isIntraPeriod
    ? !!(data.start_partial_days || data.end_partial_days)
    : !!data.partial_days;

  const seriesData = useMemo(() => {
    if (!hasPartialPeriod) {
      return { [metric]: seriesByMetric };
    }

    if (isIntraPeriod) {
      const firstPointWithValueIndex = data.start_partial_days
        ? seriesByMetric.findIndex((p) => !isNil(p[1]))
        : -1;
      const lastPointWithValueIndex = data.end_partial_days
        ? findLastPointWithValueIndex(seriesByMetric)
        : -1;

      return {
        [metric]: seriesByMetric.map(([timestamp, value], index) => ({
          x: timestamp,
          y: value,
          ...([firstPointWithValueIndex, lastPointWithValueIndex].includes(index)
            ? {
                isPartial: true,
                isStartPartial: index === firstPointWithValueIndex,
                isEndPartial: index === lastPointWithValueIndex,
              }
            : {}),
        })),
      };
    }

    const getPartialColor = (color) => `url(#custom-pattern-${color}-column)`;

    return {
      [metric]: seriesByMetric.map(([timestamp, value], index) => {
        const isLastPoint = index === seriesByMetric.length - 1;
        return {
          x: timestamp,
          y: value,
          ...(isLastPoint && hasPartialPeriod
            ? {
                isPartial: true,
                color: getPartialColor(value < 0 ? negativeColor : positiveColor),
              }
            : {}),
        };
      }),
    };
  }, [
    isIntraPeriod,
    metric,
    seriesByMetric,
    hasPartialPeriod,
    data.start_partial_days,
    data.end_partial_days,
  ]);

  const metaSeriesData = useMemo(() => {
    if (isIntraPeriod) {
      let metricSeriesData = {
        color: positiveColor,
        type: 'spline',
        visible: true,
        name: metric,
      };

      const decoratedSeries = seriesData[metric];
      if (hasPartialPeriod && decoratedSeries.length > 1) {
        const lastPointPartialIndex = decoratedSeries.findIndex((p) => p.isEndPartial);
        const firstPointPartialIndex = decoratedSeries.findIndex((p) => p.isStartPartial);

        const showEndPartial = lastPointPartialIndex > 0;
        const showStartPartial =
          firstPointPartialIndex >= 0 &&
          (showEndPartial ? firstPointPartialIndex + 1 < lastPointPartialIndex : true);

        const getZonesConfig = (partialIndex) => [
          {
            value: nth(decoratedSeries, partialIndex - 1)?.x,
          },
          {
            fillColor: `url(#custom-pattern-${positiveColor}-spline)`,
            dashStyle: 'ShortDashDotDot',
            value: nth(decoratedSeries, partialIndex)?.x,
          },
        ];

        metricSeriesData = {
          ...metricSeriesData,
          zoneAxis: 'x',
          zones: [
            ...(showStartPartial ? getZonesConfig(firstPointPartialIndex + 1) : []),
            ...(showEndPartial ? getZonesConfig(lastPointPartialIndex) : []),
          ].filter((item) => !isNil(item.value)),
        };
      }

      return {
        [metric]: metricSeriesData,
      };
    }

    return {
      [metric]: {
        color: positiveColor,
        negativeColor,
        type: 'column',
        visible: true,
        name: metric,
      },
    };
  }, [metric, isIntraPeriod, hasPartialPeriod, seriesData]);

  const chartConfig = useMemo(
    () =>
      baseDriversConfig(seriesByMetric, {
        chartData: data,
        overPeriod,
        isIntraPeriod,
        showActivePoint: !isIntraPeriod && showActivePoint,
        granularity,
        showEdgeLabels: !expanded,
        chartStyleOverride,
      }),
    [
      seriesByMetric,
      overPeriod,
      data,
      granularity,
      isIntraPeriod,
      showActivePoint,
      expanded,
      chartStyleOverride,
    ]
  );

  const legendOptions = [
    {
      label: cohortLabel,
      color: positiveColor,
    },
  ];

  return (
    <ChartLayout
      header={
        <div className="base-column-chart-header">
          {sectionKey === 'metricsChart' ? (
            <ActivityMetricDropdown
              selected={metric}
              appendButtonText={periodChangeTypeCaptures[overPeriod]}
              onChange={onMetricChange}
            />
          ) : (
            <div className="metrics-meta">
              <h6 className="h6">{baseMetrics[metric]}</h6>
              <span className="metrics-meta__capture">
                {periodChangeTypeCaptures[overPeriod] || ''}
              </span>
            </div>
          )}
          {headerContent}
          {onExpand && (
            <Button shape="square" variant="secondary" onClick={() => onExpand(!expanded)}>
              <span className={expanded ? 'hicon-minimize' : 'hicon-maximize'} />
            </Button>
          )}
        </div>
      }
    >
      <SmartChart
        chartId="drivers-chart"
        type="stockChart"
        series={seriesData}
        metaSeriesData={metaSeriesData}
        config={chartConfig}
        allowPartialPeriods={hasPartialPeriod}
      />
      {showFooter && (
        <div className="drivers-chart__footer">
          <div className="drivers-chart__legend">
            {legendOptions.map(({ label, color }) => (
              <div key={label} className="drivers-chart__legend-item">
                <span className="legend-bubble-marker" style={{ backgroundColor: color }} />
                <span>{label}</span>
              </div>
            ))}
          </div>
          <div className="highcharts-watermark" />
        </div>
      )}
    </ChartLayout>
  );
};

BaseChart.propTypes = {
  sectionKey: T.oneOf(['downloadsChart', 'metricsChart']),
  chartSectionConfig: T.shape({
    expanded: T.bool,
    metric: T.string,
  }).isRequired,
  data: T.object.isRequired,
  overPeriod: T.string.isRequired,
  granularity: T.string,
  cohortLabel: T.string,
  headerContent: T.node,
  showFooter: T.bool,
  showActivePoint: T.bool,
  chartStyleOverride: T.object,
  onMetricChange: T.func.isRequired,
  onExpand: T.func,
};

export default BaseChart;
